import type { AnyObject } from '@/components/UberForm/types'
import { StructureExportDto } from '@/endpoints/models'

/**
 * Maps history JSON (same payload as VersionDiffModal / node history detail) to flat keys
 * used by {@link useIsFieldHighlighted} on the View overview form.
 */
export function buildViewPrevVersionHighlights(dataString: string): AnyObject | null {
	if (!dataString?.trim()) return null

	let parsed: unknown
	try {
		parsed = JSON.parse(dataString)
	} catch {
		return null
	}

	if (parsed === null || typeof parsed !== 'object') return null

	let payload: Record<string, unknown> = parsed as Record<string, unknown>

	// Wrapped export: { content: StructureExportDto }
	const content = payload.content as StructureExportDto | undefined
	if (content && typeof content === 'object') {
		payload = content as unknown as Record<string, unknown>
	}

	// Tree export: find VIEW node under ROOT
	if (
		payload.type === StructureExportDto.TypeEnum.ROOT &&
		Array.isArray(payload.children)
	) {
		const viewNode = findViewNodeInExport(payload.children as StructureExportDto[])
		if (viewNode) {
			payload = viewNode as unknown as Record<string, unknown>
		}
	}

	let actual: Record<string, unknown> = payload
	if (typeof payload.actualData === 'string' && payload.actualData.trim()) {
		try {
			const inner = JSON.parse(payload.actualData)
			if (inner && typeof inner === 'object') {
				actual = inner as Record<string, unknown>
			}
		} catch {
			// keep payload-level fields
		}
	}

	const stereotype = actual.stereotype as { id?: number } | undefined
	const owner = actual.owner as { id?: number } | undefined
	const responsibleAnalyst = actual.responsibleAnalyst as { id?: number } | undefined
	const responsiblePerson = actual.responsiblePerson as { id?: number } | undefined
	const businessDomain = actual.businessDomain as { id?: number } | undefined

	const out: AnyObject = {
		name: actual.name,
		code: actual.code,
		description: actual.description,
		comment: actual.comment,
		status: actual.status,
		stereotypeId: actual.stereotypeId ?? stereotype?.id,
		ownerId: actual.ownerId ?? owner?.id,
		responsibleAnalystId: actual.responsibleAnalystId ?? responsibleAnalyst?.id,
		responsiblePersonId: actual.responsiblePersonId ?? responsiblePerson?.id,
		businessDomainId: actual.businessDomainId ?? businessDomain?.id,
		objectSettings: actual.objectSettings,
		sourceScript: actual.sourceScript,
		sourceDescription: actual.sourceDescription,
		criteriaScript: actual.criteriaScript,
		criteriaDescription: actual.criteriaDescription,
		customAttributes: actual.customAttributes,
	}

	const hasAny = Object.values(out).some((v) => v !== undefined && v !== null)
	return hasAny ? out : null
}

function findViewNodeInExport(
	children: StructureExportDto[],
): StructureExportDto | undefined {
	for (const node of children) {
		if (node.type === StructureExportDto.TypeEnum.VIEW) return node
		if (node.children?.length) {
			const found = findViewNodeInExport(node.children)
			if (found) return found
		}
	}
	return undefined
}
